import React from 'react';
import CustomHeader from '../components/CustomHeader';
import Footer from '../components/Footer';
import '../styles/screens/Privacy.css';

const Privacy: React.FC = () => {
  return (
    <div className="privacy-container">
      <CustomHeader />

      {/* Main Content */}
      <main className="privacy-content">
        <div className="container">
          <h1 className="privacy-title">Privacy Policy</h1>
          <p className="privacy-date">Last updated: August 2025</p>

          <section className="privacy-section">
            <h2>1. Introduction</h2>
            <p>
              RushHour ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your information when you use our parking assistance mobile application.
            </p>
            <p>
              By using RushHour, you agree to the collection and use of information in accordance with this policy.
            </p>
          </section>

          <section className="privacy-section">
            <h2>2. Information We Collect</h2>
            <h3>2.1 Personal Information</h3>
            <p>We collect the following personal information through Google Sign-In:</p>
            <ul>
              <li><strong>First Name</strong> - Your given name</li>
              <li><strong>Last Name</strong> - Your family name</li>
              <li><strong>Email Address</strong> - Your email address associated with your Google account</li>
            </ul>

            <h3>2.2 Vehicle Information</h3>
            <p>We collect and store the following vehicle details:</p>
            <ul>
              <li><strong>License Plate Number</strong> - Your vehicle's registration number</li>
              <li><strong>Brand</strong> - Your vehicle's manufacturer (e.g., Toyota, BMW)</li>
              <li><strong>Model</strong> - Your vehicle's specific model</li>
              <li><strong>Color</strong> - Your vehicle's color</li>
            </ul>

            <h3>2.3 Future Data Collection</h3>
            <p>In future releases, we may also collect:</p>
            <ul>
              <li><strong>GPS Location Data</strong> - For enhanced parking assistance features</li>
              <li><strong>Phone Number</strong> - For additional verification and communication</li>
              <li><strong>Additional Sign-In Methods</strong> - Facebook and Apple Sign-In options</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>3. How We Use Your Information</h2>
            <p>We use the collected information for the following purposes:</p>
            <ul>
              <li><strong>App Functionality</strong> - To provide parking assistance services and manage blocking situations</li>
              <li><strong>User Authentication</strong> - To verify your identity and secure your account</li>
              <li><strong>Vehicle Management</strong> - To help you register and manage your vehicles</li>
              <li><strong>Notifications</strong> - To send you push notifications about parking situations via Firebase Cloud Messaging (FCM)</li>
              <li><strong>Service Improvement</strong> - To analyze app usage and improve our services</li>
              <li><strong>Customer Support</strong> - To respond to your inquiries and provide assistance</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>4. Third-Party Services</h2>
            <h3>4.1 Google Services</h3>
            <ul>
              <li><strong>Google Sign-In</strong> - For user authentication and profile information</li>
              <li><strong>Google Analytics</strong> - For app usage analytics and performance monitoring</li>
            </ul>

            <h3>4.2 Firebase Services</h3>
            <ul>
              <li><strong>Firebase Analytics</strong> - For app performance and user behavior analytics</li>
              <li><strong>Firebase Cloud Messaging (FCM)</strong> - For push notifications</li>
            </ul>

            <p>
              <strong>Important:</strong> We do not share any personally identifiable information (PII) with these third-party services beyond what is necessary for the app to function.
            </p>
          </section>

          <section className="privacy-section">
            <h2>5. Data Storage and Security</h2>
            <h3>5.1 Data Storage Location</h3>
            <p>Your data is stored on a Virtual Private Server (VPS) located in Helsinki, Finland (EU-Central region).</p>

            <h3>5.2 Security Measures</h3>
            <ul>
              <li><strong>HTTPS Encryption</strong> - All data transmission is encrypted using secure HTTPS connections</li>
              <li><strong>JWT Authentication</strong> - Secure token-based authentication for all API requests</li>
              <li><strong>Secure Servers</strong> - Data is stored on secure, protected servers</li>
              <li><strong>Access Control</strong> - Limited access to user data by authorized personnel only</li>
            </ul>
          </section>

          <section className="privacy-section">
            <h2>6. Data Retention</h2>
            <p>
              We retain your personal information and vehicle data indefinitely until you make an express request for deletion. 
              This allows us to provide continuous service and maintain your app preferences and vehicle information.
            </p>
            <p>
              You can request data deletion at any time by contacting us at : 
              <a href="mailto:rush.hour.contact.us@gmail.com" className="contact-email"> rush.hour.contact.us@gmail.com</a>
            </p>
          </section>

          <section className="privacy-section">
            <h2>7. Data Sharing and Communication</h2>
            <h3>7.1 No User-to-User Communication</h3>
            <p>
              RushHour does not enable direct communication between users. All notifications and updates are sent through our backend system.
            </p>

            <h3>7.2 No Third-Party Data Sharing</h3>
            <p>
              We do not sell, trade, or otherwise transfer your personal information to third parties, except for the essential services mentioned above (Google, Firebase) that are necessary for app functionality.
            </p>

            <h3>7.3 Legal Requirements</h3>
            <p>
              We may disclose your information if required by law or in response to valid legal requests, such as subpoenas or court orders.
            </p>
          </section>

          <section className="privacy-section">
            <h2>8. Your Rights and Choices</h2>
            <h3>8.1 Data Access</h3>
            <p>You have the right to:</p>
            <ul>
              <li>Access your personal information stored in our system</li>
              <li>Update or correct your information through the app</li>
              <li>Request deletion of your data</li>
              <li>Opt out of push notifications (through device settings)</li>
            </ul>

            <h3>8.2 Data Deletion</h3>
            <p>
              To request complete deletion of your data, please send an email to 
              <a href="mailto:rush.hour.contact.us@gmail.com" className="contact-email"> rush.hour.contact.us@gmail.com </a> 
               with the subject line "Data Deletion Request" and include your email address used for the app.
            </p>
          </section>

          <section className="privacy-section">
            <h2>9. Children's Privacy</h2>
            <p>
              RushHour is not intended for use by children under the age of 13. We do not knowingly collect personal information from children under 13. 
              If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately.
            </p>
          </section>

          <section className="privacy-section">
            <h2>10. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
            </p>
            <p>
              Your continued use of the app after any changes constitutes acceptance of the updated Privacy Policy.
            </p>
          </section>

          <section className="privacy-section">
            <h2>11. International Users</h2>
            <p>
              Your data is stored in the European Union (Finland). If you are accessing RushHour from outside the EU, please be aware that your information will be transferred to, stored, and processed in the EU.
            </p>
          </section>

          <section className="privacy-section">
            <h2>12. Contact Information</h2>
            <p>
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="contact-info">
              <p><strong>Email:</strong> <a href="mailto:rush.hour.contact.us@gmail.com" className="contact-email">rush.hour.contact.us@gmail.com</a></p>
              <p><strong>Subject Line:</strong> "Privacy Policy Inquiry"</p>
              <p><strong>Response Time:</strong> We aim to respond within 48 hours</p>
            </div>
          </section>

          <section className="privacy-section">
            <h2>13. Acknowledgment</h2>
            <p>
              By using RushHour, you acknowledge that you have read and understood this Privacy Policy and agree to its terms. 
              If you do not agree with any part of this policy, please do not use our application.
            </p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Privacy;
